import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { IconChevronRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { getStripeSession, mintNft } from "../../utils/functions";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import AnimationSuccess from "../lottie-animations/AnimationSuccess";
import AnimationFailed from "../lottie-animations/AnimationFailed";
import MintingLoadingScreen from "../lottie-animations/MintingLoadingScreen";

export const MintNFT = ({ sessionId, publicAddress, nftUid, userData, nftData }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [mintingStatus, setMintingStatus] = useState(null);
    const [txHash, setTxHash] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const { dispatch } = useContext(AppContext);

    useEffect(() => {
        let isMounted = true;
        let hasStartedMinting = false;

        const processMinting = async () => {
            try {
                // Check if already minted
                if (nftData.paid && nftData.nftTxnHash) {
                    if (isMounted) {
                        setTxHash(nftData.nftTxnHash);
                        setMintingStatus('success');
                        setIsLoading(false);
                    }
                    return;
                }

                // Verify payment status
                const response = await getStripeSession({ sessionId });
                const paymentStatus = response?.data;

                if (paymentStatus !== 'complete') {
                    // Mark payment as incomplete
                    const nftRef = doc(db, 'royalty_templates', nftUid);
                    await updateDoc(nftRef, { paid: false, nftTxnHash: '' });

                    throw new Error("Payment verification failed");
                }

                // Start minting process
                if (!hasStartedMinting && isMounted) {
                    hasStartedMinting = true;

                    const handleMintStatus = (status) => {
                        if (isMounted) {
                            setMintingStatus(status === 'complete' ? 'success' : 'failed');
                        }
                    };

                    const handleTxHash = (hash) => {
                        if (isMounted && hash) {
                            setTxHash(hash);
                        }
                    };

                    // Mint the NFT
                    const result = await mintNft(
                        publicAddress,
                        nftUid,
                        handleTxHash,
                        handleMintStatus,
                        { ...nftData }
                    );

                    // Update context with result
                    if (result && isMounted) {
                        const updatedTemplates = userData.royaltyTemplates.map(template =>
                            template.uid === nftUid ? result : template
                        );

                        dispatch({
                            type: "update_royalty_templates",
                            payload: updatedTemplates
                        });
                    }
                }

                if (isMounted) {
                    setIsLoading(false);
                }

            } catch (error) {
                console.error("Minting error:", error);
                if (isMounted) {
                    setErrorMessage(error.message || "Minting failed");
                    setMintingStatus('failed');
                    setIsLoading(false);
                }
            }
        };

        processMinting();

        return () => {
            isMounted = false;
        };
    }, [sessionId, publicAddress, nftUid, nftData, userData, dispatch]);

    // Loading state
    if (isLoading) {
        return (
            <MintingLoadingScreen />
        );
    }

    // Success state
    if (mintingStatus === 'success') {
        return (
            <Stack
                direction="column"
                alignItems="center"
                spacing={2}
                sx={{
                    m: "auto",
                    p: 5,
                    borderRadius: 5,
                    boxShadow: 5,
                    bgcolor: "#FFF",
                    maxWidth: "sm"
                }}
            >
                <AnimationSuccess open />
                <Typography variant="h3" textAlign="center">
                    {nftData.title || nftData.name || "Your NFT"} has been minted!
                </Typography>
                {txHash && (
                    <>
                        <Typography sx={{ color: grey[600], textAlign: 'center' }}>
                            View your NFT on the blockchain
                        </Typography>
                        <Typography>
                            <a
                                href={`https://polygonscan.com/tx/${txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                                View on Polygonscan
                            </a>
                        </Typography>
                    </>
                )}
                <Button
                    component={Link}
                    to="/portfolio"
                    className="sign-button"
                    endIcon={<IconChevronRight />}
                    sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
                >
                    View in Portfolio
                </Button>
            </Stack>
        );
    }

    // Failed state
    return (
        <Stack
            direction="column"
            alignItems="center"
            spacing={2}
            sx={{
                m: "auto",
                p: 5,
                borderRadius: 5,
                boxShadow: 5,
                bgcolor: "#FFF",
                maxWidth: "sm"
            }}
        >
            <AnimationFailed open />
            <Typography variant="h3" textAlign="center">
                Minting Failed
            </Typography>
            <Typography sx={{ color: grey[600], textAlign: 'center' }}>
                {errorMessage || `Unable to mint ${nftData.title || nftData.name || "your NFT"}`}
            </Typography>
            <Button
                component={Link}
                to="/portfolio"
                className="sign-button"
                endIcon={<IconChevronRight />}
                sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
            >
                Try Again from Portfolio
            </Button>
        </Stack>
    );
};

